// src/hooks/useOpportunityFormatPreset.ts

import { useEffect, useMemo, useState } from "react";
import { resolveFormatCalcLines } from "@/lib/opportunity/formatCalcLineResolver";
import { resolveFormatDepositRule } from "@/lib/opportunity/formatDepositResolver";
import { resolveFormatSettings } from "@/lib/opportunity/formatSettingsResolver";
import type { FormatCalcLine } from "@/lib/opportunity/formatCalcLineTypes";
import {
  DEFAULT_FORMAT_DEPOSIT_RULE,
  type FormatDepositRuleDef,
} from "@/lib/opportunity/formatDepositTypes";
import { DEFAULT_BASE_AMOUNT_LABEL } from "@/lib/opportunity/formatSettingsTypes";
import type { FormatPresetPricingContext } from "@/lib/opportunity/formatPresetPricing";
import type { OpportunityFormatKey } from "@/types/opportunity";

export function useOpportunityFormatPreset(args: {
  formatKey: OpportunityFormatKey;
  productType: string;
}) {
  const { formatKey, productType } = args;
  const [calcLines, setCalcLines] = useState<FormatCalcLine[]>([]);
  const [depositRule, setDepositRule] = useState<FormatDepositRuleDef>(DEFAULT_FORMAT_DEPOSIT_RULE);
  const [baseAmountLabel, setBaseAmountLabel] = useState(DEFAULT_BASE_AMOUNT_LABEL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const [lines, rule, settings] = await Promise.all([
        resolveFormatCalcLines({ formatKey, productType }),
        resolveFormatDepositRule({ formatKey, productType }),
        resolveFormatSettings({ formatKey, productType }),
      ]);
      if (cancelled) return;
      setCalcLines(lines);
      setDepositRule(rule);
      setBaseAmountLabel(settings.base_amount_label);
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [formatKey, productType]);

  const preset = useMemo<FormatPresetPricingContext>(
    () => ({ calcLines, depositRule, baseAmountLabel }),
    [calcLines, depositRule, baseAmountLabel],
  );

  return { calcLines, depositRule, baseAmountLabel, preset, loading, error };
}
